import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api from '../utils/api';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Users, DollarSign, TrendingUp, Copy, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Referrals = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newReferralEmail, setNewReferralEmail] = useState('');
  const [copiedCode, setCopiedCode] = useState(false);

  useEffect(() => {
    fetchReferralStats();
  }, []);

  const fetchReferralStats = async () => {
    try {
      const response = await api.get('/referrals/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Failed to load referral stats');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyCode = () => {
    navigator.clipboard.writeText(stats.referral_code);
    setCopiedCode(true);
    toast.success('Referral code copied to clipboard!');
    setTimeout(() => setCopiedCode(false), 2000);
  };

  const handleSendReferral = async (e) => {
    e.preventDefault();
    try {
      await api.post('/referrals/create', { referred_email: newReferralEmail });
      toast.success('Referral invitation sent!');
      setNewReferralEmail('');
      fetchReferralStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send referral');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading referrals...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Navbar />

      <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full">
        <div className="mb-8">
          <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2" data-testid="referrals-title">
            Referral Network
          </h1>
          <p className="text-slate-600">Grow your network and earn rewards for bringing in verified carriers</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white border border-slate-200 rounded-md p-6 shadow-sm"
            data-testid="total-referrals-stat"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-50 p-3 rounded-md">
                <Users className="h-6 w-6 text-blue-500" />
              </div>
              <h3 className="font-semibold text-slate-900">Total Referrals</h3>
            </div>
            <div className="text-3xl font-bold font-heading text-slate-900">
              {stats?.total_referrals || 0}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-white border border-slate-200 rounded-md p-6 shadow-sm"
            data-testid="successful-referrals-stat"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-green-50 p-3 rounded-md">
                <CheckCircle className="h-6 w-6 text-green-500" />
              </div>
              <h3 className="font-semibold text-slate-900">Successful</h3>
            </div>
            <div className="text-3xl font-bold font-heading text-slate-900">
              {stats?.successful_referrals || 0}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white border border-slate-200 rounded-md p-6 shadow-sm"
            data-testid="earnings-stat"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-purple-50 p-3 rounded-md">
                <DollarSign className="h-6 w-6 text-purple-500" />
              </div>
              <h3 className="font-semibold text-slate-900">Total Earned</h3>
            </div>
            <div className="text-3xl font-bold font-heading text-slate-900">
              ${stats?.total_earned?.toFixed(2) || '0.00'}
            </div>
          </motion.div>
        </div>

        {/* Referral Code */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-md p-8 mb-8 shadow-lg"
        >
          <h2 className="font-heading text-2xl font-bold mb-2">Your Unique Referral Code</h2>
          <p className="text-blue-100 mb-6">Share this code with carriers you want to invite to the network</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 bg-white rounded-md p-4 flex items-center justify-between">
              <code className="font-mono text-2xl font-bold text-blue-600">
                {stats?.referral_code}
              </code>
              <button
                onClick={handleCopyCode}
                className="bg-blue-50 text-blue-600 px-4 py-2 rounded-md font-semibold hover:bg-blue-100 transition-all flex items-center gap-2"
                data-testid="copy-code-btn"
              >
                {copiedCode ? <CheckCircle className="h-5 w-5" /> : <Copy className="h-5 w-5" />}
                {copiedCode ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </motion.div>

        {/* Send Referral */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white border border-slate-200 rounded-md p-6 shadow-sm mb-8"
        >
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Invite a Carrier</h2>
          <form onSubmit={handleSendReferral} className="flex flex-col sm:flex-row gap-4" data-testid="referral-form">
            <div className="flex-1 relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="email"
                value={newReferralEmail}
                onChange={(e) => setNewReferralEmail(e.target.value)}
                placeholder="friend@example.com"
                required
                className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                data-testid="referral-email-input"
              />
            </div>
            <button
              type="submit"
              className="bg-slate-900 text-white px-6 py-3 rounded-md font-semibold hover:bg-slate-800 transition-all btn-hover-lift"
              data-testid="send-referral-btn"
            >
              Send Invitation
            </button>
          </form>
        </motion.div>

        {/* Referral List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white border border-slate-200 rounded-md p-6 shadow-sm"
        >
          <h2 className="font-heading text-xl font-bold text-slate-900 mb-4">Your Referrals</h2>
          {stats?.referrals && stats.referrals.length > 0 ? (
            <div className="space-y-3">
              {stats.referrals.map((referral) => (
                <div
                  key={referral.id || referral.referred_email}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-md"
                  data-testid={`referral-${referral.id || referral.referred_email}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${
                      referral.status === 'completed' ? 'bg-green-500' :
                      referral.status === 'pending' ? 'bg-yellow-500' :
                      'bg-slate-300'
                    }`}></div>
                    <div>
                      <p className="font-medium text-slate-900">{referral.referred_email}</p>
                      <p className="text-sm text-slate-600 capitalize">{referral.status}</p>
                    </div>
                  </div>
                  {referral.reward_earned > 0 && (
                    <span className="font-semibold text-green-600">
                      +${referral.reward_earned.toFixed(2)}
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No referrals yet. Start inviting carriers to grow your network!</p>
            </div>
          )}
        </motion.div>
      </main>

      <Footer />
    </div>
  );
};

export default Referrals;